import { ChevronRight, Home } from "lucide-react";
import type { BaseComponentProps } from '../../types/puck';
import { EmptyBlock } from "../empty-block";
import { SmartLink } from "../smart-link";
import { cn } from "../../lib/utils";

interface BreadcrumbItem {
  label: string;
  url?: string;
  /** @deprecated Use `url` instead. Kept for backward-compat with existing page data. */
  href?: string;
}

interface BreadcrumbProps extends BaseComponentProps {
  items: BreadcrumbItem[];
  showHomeIcon: boolean;
  separator: "chevron" | "slash" | "arrow";
  baseUrl?: string;
}

const toAbsoluteUrl = (url: string, baseUrl: string): string | null => {
    try {
      return new URL(url, baseUrl || undefined).href;
    } catch {
      return null;
    }
  },
  toBreadcrumbSchema = (items: BreadcrumbItem[], baseUrl: string) => {
    const itemListElement = [];
    for (const [index, crumb] of items.entries()) {
      const isLinked = Boolean(crumb.url && crumb.url !== "#"),
        item = isLinked ? toAbsoluteUrl(crumb.url!, baseUrl) : null;
      if (isLinked && !item) {
        return null;
      }
      itemListElement.push({
        "@type": "ListItem",
        name: crumb.label,
        position: index + 1,
        ...(item && { item }),
      });
    }

    if (itemListElement.length > 0) {
      return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement };
    }
    return null;
  },

  Breadcrumb: React.FC<BreadcrumbProps> = ({
    items,
    showHomeIcon = true,
    separator = "chevron",
    baseUrl = "",
    id,
    className,
  }) => {
    // Normalize items so `href` (legacy) is merged into `url`
    const normalizedItems = items?.map((item) => ({
        ...item,
        url: item.url || item.href,
      })),

      breadcrumbSchema = toBreadcrumbSchema(normalizedItems ?? [], baseUrl),

      renderSeparator = () => {
        switch (separator) {
          case "slash": {
            return <span className="jobsite:mx-2 jobsite:text-muted-foreground">/</span>;
          }
          case "arrow": {
            return <span className="jobsite:mx-2 jobsite:text-muted-foreground">→</span>;
          }
          default: {
            return (
              <ChevronRight className="jobsite:w-4 jobsite:h-4 jobsite:mx-2 jobsite:text-muted-foreground" />
            );
          }
        }
      };

    if (!normalizedItems || normalizedItems.length === 0) {
      return <EmptyBlock label="No links yet — add them in the panel" />;
    }

    return (
      <nav
        id={id}
        aria-label="Breadcrumb"
        className={cn("jobsite:py-4 jobsite:px-4 jobsite:bg-muted/50", className)}
      >
        {/* Breadcrumb Schema JSON-LD */}
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}

        <div className="jobsite:container jobsite:mx-auto">
          <ol className="jobsite:flex jobsite:items-center jobsite:flex-wrap jobsite:text-sm">
            {normalizedItems.map((item, index) => {
              const isFirst = index === 0,
                isLast = index === normalizedItems.length - 1;

              return (
                <li key={index} className="jobsite:flex jobsite:items-center">
                  {/* Separator (not before first item) */}
                  {!isFirst && renderSeparator()}

                  {/* Breadcrumb item */}
                  {item.url && item.url !== "#" && !isLast ? (
                    <SmartLink
                      to={item.url}
                      className={cn(
                        "jobsite:flex jobsite:items-center jobsite:gap-1.5",
                        "jobsite:text-muted-foreground jobsite:hover:text-foreground",
                        "jobsite:transition-colors jobsite:no-underline",
                      )}
                    >
                      {isFirst && showHomeIcon && <Home className="jobsite:w-4 jobsite:h-4" />}
                      <span>{item.label}</span>
                    </SmartLink>
                  ) : (
                    <span
                      className={cn(
                        "jobsite:flex jobsite:items-center jobsite:gap-1.5",
                        isLast
                          ? "jobsite:text-foreground jobsite:font-medium"
                          : "jobsite:text-muted-foreground",
                      )}
                      aria-current={isLast ? "page" : undefined}
                    >
                      {isFirst && showHomeIcon && <Home className="jobsite:w-4 jobsite:h-4" />}
                      <span>{item.label}</span>
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    );
  };

export type { BreadcrumbItem, BreadcrumbProps };
export { Breadcrumb };
